import { z } from 'zod';
import { brainRowProvenanceSchema } from './brain-row-provenance.js';
// Spec §3.1c — Brand Design System (DS).
//
// The visual half of the brain. Templates + dynamic-layout
// primitives BOTH read every visual choice from this single source
// of truth. Constants above the DS layer become bugs (Phase 3 ships
// the lint rule that enforces it).
//
// This file is the SCHEMA + DEFAULTS. The Phase 1 defaults are tuned
// to match the values today's primitives currently hard-code, so a
// brain row with a default DS renders identically to a brain row
// pre-DS. Phases 2+ replace defaults with real extracted values from
// the visual scrape, gated through the §3.1b confidence + draft
// regime.
//
// Zod 4 caveat: .default() returns its argument AS-IS without
// re-parsing, so a nested .default({}) drops sibling leaf defaults
// on the floor. We construct each section's defaults as concrete
// pre-parsed constants in topological order and feed them back as
// the parent's .default(…). DEFAULT_BRAND_DESIGN_SYSTEM at the
// bottom is the same constants composed together.
// ─── typography ─────────────────────────────────────────────────────
export const typographyCaseSchema = z.enum(['lowercase', 'TitleCase', 'UPPERCASE', 'sentence']);
export const typographyScaleLevelsSchema = z.object({
    h1: z.number().int().min(8).max(200).default(48),
    h2: z.number().int().min(8).max(160).default(32),
    h3: z.number().int().min(8).max(120).default(24),
    body: z.number().int().min(8).max(48).default(16),
    small: z.number().int().min(8).max(32).default(13),
    micro: z.number().int().min(6).max(24).default(11),
});
const TYPOGRAPHY_SCALE_LEVELS_DEFAULTS = typographyScaleLevelsSchema.parse({});
export const typographyScaleSchema = z.object({
    base: z.number().int().min(8).max(32).default(16),
    ratio: z.number().min(1).max(2).default(1.25),
    levels: typographyScaleLevelsSchema.default(TYPOGRAPHY_SCALE_LEVELS_DEFAULTS),
});
const TYPOGRAPHY_SCALE_DEFAULTS = typographyScaleSchema.parse({});
export const typographyWeightsSchema = z.object({
    display: z.number().int().min(100).max(900).default(700),
    body: z.number().int().min(100).max(900).default(400),
    /** s5g.6 — italic-accent weight. Default 400 matches the canonical
     *  bold-sans + REGULAR-italic-serif gestalt most brands use. Brands
     *  whose italic display IS heavy (rare) override via DS edit or
     *  vision-pass fold. NOT the h3 computed-style weight: a brand's h3
     *  is typically bold (matching display) which is the wrong signal
     *  for the italic-accent clause weight. */
    accent: z.number().int().min(100).max(900).default(400),
});
const TYPOGRAPHY_WEIGHTS_DEFAULTS = typographyWeightsSchema.parse({});
export const typographyCaseRulesSchema = z.object({
    headline: typographyCaseSchema.default('sentence'),
    body: typographyCaseSchema.default('sentence'),
    eyebrow: typographyCaseSchema.default('UPPERCASE'),
});
const TYPOGRAPHY_CASE_RULES_DEFAULTS = typographyCaseRulesSchema.parse({});
export const typographyLetterSpacingSchema = z.object({
    display: z.number().min(-0.1).max(0.5).default(-0.02),
    body: z.number().min(-0.1).max(0.5).default(0),
    micro: z.number().min(-0.1).max(0.5).default(0.14),
});
const TYPOGRAPHY_LETTER_SPACING_DEFAULTS = typographyLetterSpacingSchema.parse({});
export const typographyLineHeightSchema = z.object({
    display: z.number().min(0.8).max(2).default(1.1),
    body: z.number().min(0.8).max(2).default(1.45),
    micro: z.number().min(0.8).max(2).default(1.4),
});
const TYPOGRAPHY_LINE_HEIGHT_DEFAULTS = typographyLineHeightSchema.parse({});
export const typographySystemSchema = z.object({
    display: z.string().min(1).max(120).default('system-ui'),
    body: z.string().min(1).max(120).default('system-ui'),
    mono: z.string().min(1).max(120).default('ui-monospace'),
    accent: z.string().min(1).max(120).default('system-ui'),
    scale: typographyScaleSchema.default(TYPOGRAPHY_SCALE_DEFAULTS),
    weights: typographyWeightsSchema.default(TYPOGRAPHY_WEIGHTS_DEFAULTS),
    caseRules: typographyCaseRulesSchema.default(TYPOGRAPHY_CASE_RULES_DEFAULTS),
    letterSpacing: typographyLetterSpacingSchema.default(TYPOGRAPHY_LETTER_SPACING_DEFAULTS),
    lineHeight: typographyLineHeightSchema.default(TYPOGRAPHY_LINE_HEIGHT_DEFAULTS),
    hierarchyPosture: z.enum(['tight', 'spacious']).default('spacious'),
});
const TYPOGRAPHY_DEFAULTS = typographySystemSchema.parse({});
// ─── palette ────────────────────────────────────────────────────────
const hexSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const paletteColorRoleSchema = z.enum([
    'primary',
    'secondary',
    'accentAlt',
    'ink',
    'paper',
    'support',
]);
export const palettePairRuleSchema = z.object({
    a: paletteColorRoleSchema,
    b: paletteColorRoleSchema,
    allowAdjacent: z.boolean(),
    bufferRole: paletteColorRoleSchema.optional(),
});
export const paletteContrastRuleSchema = z.object({
    fg: paletteColorRoleSchema,
    bg: paletteColorRoleSchema,
    minRatio: z.number().min(1).max(21).default(4.5),
});
export const paletteSystemSchema = z.object({
    primary: hexSchema.default('#0F0E17'),
    ink: hexSchema.default('#0F0E17'),
    paper: hexSchema.default('#F4EFE6'),
    secondary: hexSchema.optional(),
    accentAlt: hexSchema.optional(),
    support: z.array(hexSchema).max(6).default([]),
    pairRules: z.array(palettePairRuleSchema).default([]),
    contrast: z.array(paletteContrastRuleSchema).default([]),
});
const PALETTE_DEFAULTS = paletteSystemSchema.parse({});
// ─── spacing ────────────────────────────────────────────────────────
export const spacingSystemSchema = z.object({
    base: z.number().int().min(2).max(16).default(8),
    scale: z
        .array(z.number().int().positive())
        .min(3)
        .max(12)
        .default([4, 8, 12, 16, 24, 32, 48, 64]),
    paddingPosture: z.enum(['tight', 'spacious']).default('spacious'),
    sectionGap: z.number().int().min(0).max(160).default(40),
});
const SPACING_DEFAULTS = spacingSystemSchema.parse({});
// ─── radius ─────────────────────────────────────────────────────────
export const radiusPostureSchema = z.enum(['sharp', 'soft', 'rounded', 'pill', 'mixed']);
export const radiusScaleSchema = z.object({
    button: z.number().int().min(0).max(999).default(8),
    card: z.number().int().min(0).max(64).default(12),
    image: z.number().int().min(0).max(64).default(8),
    badge: z.number().int().min(0).max(999).default(999),
});
const RADIUS_SCALE_DEFAULTS = radiusScaleSchema.parse({});
export const radiusSystemSchema = z.object({
    posture: radiusPostureSchema.default('soft'),
    scale: radiusScaleSchema.default(RADIUS_SCALE_DEFAULTS),
});
const RADIUS_DEFAULTS = radiusSystemSchema.parse({});
// ─── borders ────────────────────────────────────────────────────────
export const borderStyleSchema = z.enum(['none', 'solid', 'dotted', 'dashed']);
export const borderPerPrimitiveSchema = z.object({
    card: borderStyleSchema.default('solid'),
    divider: borderStyleSchema.default('solid'),
    imageFrame: borderStyleSchema.default('none'),
});
const BORDER_PER_PRIMITIVE_DEFAULTS = borderPerPrimitiveSchema.parse({});
export const borderSystemSchema = z.object({
    weight: z.number().min(0.25).max(8).default(1),
    style: borderStyleSchema.default('solid'),
    perPrimitive: borderPerPrimitiveSchema.default(BORDER_PER_PRIMITIVE_DEFAULTS),
});
const BORDER_DEFAULTS = borderSystemSchema.parse({});
// ─── alignment ──────────────────────────────────────────────────────
export const alignmentSystemSchema = z.object({
    headline: z.enum(['left-rag', 'center', 'justified']).default('left-rag'),
    cta: z.enum(['right', 'center', 'inline']).default('right'),
    imageText: z.enum(['image-first', 'text-first', 'overlay']).default('image-first'),
});
const ALIGNMENT_DEFAULTS = alignmentSystemSchema.parse({});
// ─── shadows ────────────────────────────────────────────────────────
export const shadowPostureSchema = z.enum(['none', 'subtle', 'pronounced', 'hard-offset']);
export const shadowPerPrimitiveSchema = z.object({
    card: shadowPostureSchema.default('subtle'),
    button: shadowPostureSchema.default('none'),
});
const SHADOW_PER_PRIMITIVE_DEFAULTS = shadowPerPrimitiveSchema.parse({});
export const shadowSystemSchema = z.object({
    posture: shadowPostureSchema.default('subtle'),
    perPrimitive: shadowPerPrimitiveSchema.default(SHADOW_PER_PRIMITIVE_DEFAULTS),
});
const SHADOW_DEFAULTS = shadowSystemSchema.parse({});
// ─── surfaces ───────────────────────────────────────────────────────
export const surfaceTreatmentSchema = z.enum(['solid', 'textured', 'gradient', 'noise']);
export const surfaceSystemSchema = z.object({
    treatment: surfaceTreatmentSchema.default('solid'),
    layering: z.boolean().default(false),
});
const SURFACE_DEFAULTS = surfaceSystemSchema.parse({});
// ─── motifs ─────────────────────────────────────────────────────────
//
// s5a — full §3.1c Phase 5 motif model. Pre-s5a the schema assumed
// motif === SVG vector path. Real brand motifs are broader: raster
// textures (paper grain, watercolor), parametric CSS patterns
// (stripes/dots/grids), gradient recipes, type ornaments (a single
// glyph from the brand display face), or R2-hosted asset references.
// The schema discriminates on `kind` so each render path knows how to
// consume its payload.
//
// Placements: where the motif can attach to a creative. Anchor lists
// per template are declared in template-meta; render time intersects
// motif.compatiblePlacements with the template's allowed anchors.
export const motifPlacementSchema = z.enum([
    'below-headline',
    'corner-stamp',
    'inline-accent',
    'background-pattern',
    'divider', // horizontal mark between content sections
    'frame', // border / outline around hero content
    'mask-edge', // shape applied to image edges (clip path)
]);
export const motifSizeTokenSchema = z.union([
    z.number().int().positive().max(1024),
    z.object({ ratio: z.number().min(0.001).max(2) }),
]);
export const motifStatusSchema = z.enum(['draft', 'auto', 'edited', 'manual', 'stale']);
// ─── motif payload kinds (discriminated by `kind`) ─────────────────
/** Hand-drawn / vector mark. The original §3.1c Phase 1 shape. */
export const motifSvgVectorPayloadSchema = z.object({
    kind: z.literal('svg-vector'),
    svg: z.string().min(1).max(20_000),
});
/** Raster texture (PNG/JPG/WEBP) — paper grain, watercolor wash,
 *  pencil noise. R2-hosted; the render primitive uses CSS
 *  background-image with blendMode + opacity for legibility. */
export const motifRasterTexturePayloadSchema = z.object({
    kind: z.literal('raster-texture'),
    url: z.string().url(),
    blendMode: z.enum(['multiply', 'overlay', 'screen', 'soft-light', 'normal']).default('multiply'),
    opacity: z.number().min(0).max(1).default(0.4),
});
/** Parametric CSS pattern. Canned recipes the render primitive
 *  knows how to draw via repeating-linear-gradient / radial-gradient.
 *  Cheaper than serving an SVG file + can re-tint via brand palette. */
export const motifPatternParamsSchema = z.object({
    /** Distance between repeating elements, in px or as ratio of shortEdge. */
    gap: motifSizeTokenSchema.default({ ratio: 0.04 }),
    /** Rotation for stripes / grids (degrees). Ignored by 'dots'. */
    angle: z.number().min(0).max(360).default(0),
    /** Stroke / dot weight, in px or ratio. */
    weight: motifSizeTokenSchema.default({ ratio: 0.005 }),
});
export const motifCssPatternPayloadSchema = z.object({
    kind: z.literal('css-pattern'),
    recipe: z.enum(['dots', 'stripes', 'grid', 'crosshatch', 'ikat']),
    params: motifPatternParamsSchema.default(motifPatternParamsSchema.parse({})),
});
/** Signature gradient — multi-stop, linear or radial. Renders as the
 *  motif's "atmosphere" (typically background-pattern placement). */
export const motifGradientStopSchema = z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    position: z.number().min(0).max(1),
});
export const motifCssGradientPayloadSchema = z.object({
    kind: z.literal('css-gradient'),
    gradientType: z.enum(['linear', 'radial']).default('linear'),
    stops: z.array(motifGradientStopSchema).min(2).max(8),
    angle: z.number().min(0).max(360).default(135),
});
/** A single character from the brand's display / accent / mono face,
 *  used as a type ornament (drop-cap, inline glyph, separator). */
export const motifGlyphPayloadSchema = z.object({
    kind: z.literal('glyph'),
    character: z.string().min(1).max(4), // single glyph (may be multi-byte emoji)
    fontRole: z.enum(['display', 'mono', 'accent', 'body']).default('display'),
});
/** R2-hosted brand asset used as a motif — e.g., an ornament extracted
 *  from the brand's site, a logo-derived stamp, a hand-cropped
 *  signature mark. Optional CSS transform for mask / tint effects. */
export const motifAssetRefPayloadSchema = z.object({
    kind: z.literal('asset-ref'),
    url: z.string().url(),
    transform: z.enum(['none', 'mask', 'tint']).default('none'),
});
export const motifPayloadSchema = z.discriminatedUnion('kind', [
    motifSvgVectorPayloadSchema,
    motifRasterTexturePayloadSchema,
    motifCssPatternPayloadSchema,
    motifCssGradientPayloadSchema,
    motifGlyphPayloadSchema,
    motifAssetRefPayloadSchema,
]);
/** s5g — motif animation spec for video templates. Static templates
 *  ignore this; Remotion templates read it + animate per anchor.
 *  durationMs is bounded so a long draw-on can't extend past a slide's
 *  natural duration. */
export const motifAnimationSchema = z.object({
    kind: z.enum(['draw-on', 'fade-in', 'pulse', 'wipe', 'none']).default('fade-in'),
    durationMs: z.number().int().min(50).max(4000).default(600),
});
export const motifSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(80),
    /** s5a — discriminated payload replaces the old `svg: string` field.
     *  Each kind has its own render path in BrandMotif (s5b). */
    payload: motifPayloadSchema,
    defaultPlacement: motifPlacementSchema.default('below-headline'),
    defaultSizeToken: motifSizeTokenSchema.default({ ratio: 0.12 }),
    defaultColorRole: paletteColorRoleSchema.default('primary'),
    /** s5a — which template anchors this motif fits into. Not every
     *  motif works at every placement (a 24x24 corner stamp doesn't
     *  belong as background-pattern; a 1920px gradient doesn't belong
     *  as inline-accent). Render time intersects this with the
     *  template's declared `motifAnchors` array. */
    compatiblePlacements: z.array(motifPlacementSchema).min(1).default(['below-headline']),
    /** s5g — optional animation spec. When unset, static templates
     *  render the motif as-is; video templates fall back to a soft
     *  fade-in default. */
    animation: motifAnimationSchema.optional(),
    /** s5d — when a vision pass classified this motif as one of the
     *  canned library entries (5c), this is the library's id. Founder
     *  uploads + manual motifs leave this unset. Lets the dashboard
     *  group / preview by library family. */
    libraryMotifId: z.string().max(80).optional(),
    status: motifStatusSchema,
    confidence: z.number().min(0).max(1).default(0.7),
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** s5e — DS-level density control. Templates render N motifs at
 *  their allowed anchors based on this posture; 'quiet' means at
 *  most one anchor per template; 'busy' allows multiple. */
export const motifDensitySchema = z.enum(['quiet', 'standard', 'busy']);
// ─── composite ──────────────────────────────────────────────────────
export const illustrationBiasSchema = z.enum(['illustration-first', 'photo-first', 'mixed']);
export const brandDesignSystemSchema = z.object({
    typography: typographySystemSchema.default(TYPOGRAPHY_DEFAULTS),
    palette: paletteSystemSchema.default(PALETTE_DEFAULTS),
    spacing: spacingSystemSchema.default(SPACING_DEFAULTS),
    radius: radiusSystemSchema.default(RADIUS_DEFAULTS),
    borders: borderSystemSchema.default(BORDER_DEFAULTS),
    alignment: alignmentSystemSchema.default(ALIGNMENT_DEFAULTS),
    shadows: shadowSystemSchema.default(SHADOW_DEFAULTS),
    surfaces: surfaceSystemSchema.default(SURFACE_DEFAULTS),
    motifs: z.array(motifSchema).default([]),
    /** s5e — how many anchors per template render motifs. Defaults to
     *  'standard' (one motif per template at the highest-priority
     *  matching anchor); 'quiet' suppresses to zero unless the brief
     *  asks; 'busy' allows multiple anchors to render concurrently. */
    motifDensity: motifDensitySchema.default('standard'),
    illustrationBias: illustrationBiasSchema.default('mixed'),
});
/** Concrete default DS — exported so primitives that need a non-null
 *  fallback (before a brand has populated their DS) can use this
 *  instead of re-parsing the schema. Stays in sync with every leaf's
 *  `.default(…)` because it goes through the parser. */
export const DEFAULT_BRAND_DESIGN_SYSTEM = brandDesignSystemSchema.parse({});
//# sourceMappingURL=brand-design-system.js.map